import { z } from 'zod';

export const BANK_ACCOUNT_TYPES = [
  'depository',
  'credit',
  'investment',
  'loan',
] as const;
export type BankAccountType = (typeof BANK_ACCOUNT_TYPES)[number];

export const MATCH_STATUSES = [
  'unmatched',
  'matched',
  'reconciled',
  'excluded',
] as const;
export type MatchStatus = (typeof MATCH_STATUSES)[number];

export const RULE_MATCH_FIELDS = [
  'description',
  'merchant_name',
  'description_or_merchant',
  'amount_range',
  'description_and_amount',
] as const;
export type RuleMatchField = (typeof RULE_MATCH_FIELDS)[number];

export const RULE_MATCH_TYPES = [
  'contains',
  'starts_with',
  'ends_with',
  'exact',
  'regex',
] as const;
export type RuleMatchType = (typeof RULE_MATCH_TYPES)[number];

// ── Bank account ─────────────────────────────────────────────────────────────

export const createMockBankAccountInputSchema = z.object({
  glAccountId: z.string().uuid(),
  institutionName: z.string().trim().min(1).max(100),
  accountName: z.string().trim().min(1).max(100),
  accountMask: z.string().trim().max(8).optional(),
  accountType: z.enum(BANK_ACCOUNT_TYPES).default('depository'),
  accountSubtype: z.string().trim().max(50).optional(),
});
export type CreateMockBankAccountInput = z.infer<
  typeof createMockBankAccountInputSchema
>;

export const createManualBankAccountInputSchema = z.object({
  glAccountId: z.string().uuid(),
  institutionName: z.string().trim().max(100).optional(),
  accountName: z.string().trim().min(1).max(100),
  accountMask: z.string().trim().max(8).optional(),
  accountType: z.enum(BANK_ACCOUNT_TYPES).default('depository'),
  accountSubtype: z.string().trim().max(50).optional(),
});
export type CreateManualBankAccountInput = z.infer<
  typeof createManualBankAccountInputSchema
>;

/** Edit an existing bank account — chiefly to correct the GL account it ties to. */
export const updateBankAccountInputSchema = z.object({
  glAccountId: z.string().uuid().optional(),
  institutionName: z.string().trim().max(100).nullable().optional(),
  accountName: z.string().trim().min(1).max(100).optional(),
  accountMask: z.string().trim().max(8).nullable().optional(),
  accountType: z.enum(BANK_ACCOUNT_TYPES).optional(),
  accountSubtype: z.string().trim().max(50).nullable().optional(),
});
export type UpdateBankAccountInput = z.infer<typeof updateBankAccountInputSchema>;

/** Per-account roll-up for the Bank Transactions overview cards. */
export interface BankAccountSummary {
  bankAccountId: string;
  /** Transactions still needing attention (match_status = 'unmatched'). */
  unmatchedCount: number;
  /** Matched + reconciled (already booked). */
  matchedCount: number;
  excludedCount: number;
  /** Σ of the Plaid `amount` over unmatched transactions (NUMERIC string, 4dp). */
  unmatchedAmountSum: string;
}

/** Direction of a manually-entered transaction (in the user's plain terms). */
export const MANUAL_TX_DIRECTIONS = ['out', 'in'] as const;
export type ManualTxDirection = (typeof MANUAL_TX_DIRECTIONS)[number];

export const addManualTransactionInputSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD'),
  description: z.string().trim().min(1).max(200),
  merchantName: z.string().trim().max(200).nullable().optional(),
  direction: z.enum(MANUAL_TX_DIRECTIONS), // out = money left account, in = money received
  amount: z
    .string()
    .regex(/^\d+(\.\d{1,4})?$/, 'must be a positive amount')
    .refine((v) => Number(v) > 0, 'must be greater than zero'),
});
export type AddManualTransactionInput = z.infer<typeof addManualTransactionInputSchema>;

export const exchangePlaidPublicTokenInputSchema = z.object({
  publicToken: z.string().min(1),
  glAccountId: z.string().uuid(),  // GL account this bank ties into
  plaidAccountId: z.string().min(1),
  institutionName: z.string().optional(),
  accountName: z.string().optional(),
  accountMask: z.string().optional(),
  accountType: z.enum(BANK_ACCOUNT_TYPES).optional(),
  accountSubtype: z.string().optional(),
});
export type ExchangePlaidPublicTokenInput = z.infer<
  typeof exchangePlaidPublicTokenInputSchema
>;

export const bankAccountSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  accountId: z.string().uuid(),
  institutionName: z.string().nullable(),
  accountName: z.string(),
  accountMask: z.string().nullable(),
  accountType: z.enum(BANK_ACCOUNT_TYPES),
  accountSubtype: z.string().nullable(),
  isMock: z.boolean(),
  isManual: z.boolean(),
  isActive: z.boolean(),
  lastSyncedAt: z.string().nullable(),
  lastSyncError: z.string().nullable(),
  // Balance as the bank reports it, captured at the last sync. Independent of
  // the GL — this is what a book balance gets compared AGAINST. Null for
  // manual/mock accounts and for any account not yet synced since this shipped.
  plaidCurrentBalance: z.string().nullable(),
  plaidAvailableBalance: z.string().nullable(),
  plaidBalanceAsOf: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BankAccount = z.infer<typeof bankAccountSchema>;

// ── Bank transaction ─────────────────────────────────────────────────────────

export const bankTransactionSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  bankAccountId: z.string().uuid(),
  date: z.string(),
  amount: z.string(),                  // money as string
  description: z.string(),
  merchantName: z.string().nullable(),
  pending: z.boolean(),
  matchStatus: z.enum(MATCH_STATUSES),
  excludedReason: z.string().nullable(),
  matchedJournalEntryId: z.string().uuid().nullable(),
  matchedByRuleId: z.string().uuid().nullable(),
  aiSuggestedAccountId: z.string().uuid().nullable(),
  aiConfidence: z.string().nullable(),
  aiReasoning: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BankTransaction = z.infer<typeof bankTransactionSchema>;

/**
 * A split amount is SIGNED. Positive posts to the transaction's natural offset
 * side; negative posts to the opposite side. A negative line is how a fee
 * withheld from a deposit is expressed: a $200 receivable collected through
 * Stripe that lands as a $193.70 deposit splits as A/R 200.00 and
 * merchant fees -6.30, which nets to the 193.70 that actually hit the bank.
 */
const splitAmount = z
  .string()
  .regex(/^-?\d+(\.\d{1,4})?$/, 'must be a money value')
  .refine((v) => Number(v) !== 0, 'must not be zero');

/** One line of a split match: a GL account and the portion of the transaction posted to it. */
export const matchSplitSchema = z.object({
  accountId: z.string().uuid(),
  amount: splitAmount,
  contactId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  classId: z.string().uuid().optional(),
  memo: z.string().max(500).optional(),
});
export type MatchSplit = z.infer<typeof matchSplitSchema>;

export const matchBankTransactionInputSchema = z
  .object({
    accountId: z.string().uuid().optional(), // single offset account (when not splitting)
    contactId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    classId: z.string().uuid().optional(),
    memo: z.string().max(500).optional(),
    /** Split the transaction across multiple accounts; their amounts must sum to the total. */
    splits: z.array(matchSplitSchema).min(1).optional(),
  })
  .refine(
    (d) => Boolean(d.accountId) !== Boolean(d.splits && d.splits.length > 0),
    'Provide either an offset account or splits, not both.',
  );
export type MatchBankTransactionInput = z.infer<
  typeof matchBankTransactionInputSchema
>;

/** Link a bank transaction to an EXISTING uncleared journal entry (no new JE). */
export const matchExistingInputSchema = z.object({
  journalEntryId: z.string().uuid(),
});
export type MatchExistingInput = z.infer<typeof matchExistingInputSchema>;

/**
 * Settle an open receivable with this deposit. Posts DR bank / CR A/R against
 * the accrual line identified by `receivableLineId` — revenue is NOT recognised
 * again, having already been recognised when the receivable was accrued.
 */
export const settleReceivableInputSchema = z.object({
  receivableLineId: z.string().uuid(),
});
export type SettleReceivableInput = z.infer<typeof settleReceivableInputSchema>;

/**
 * A way to clear a bank transaction without free-form categorizing:
 *  - kind 'existing'      → link to an entry already posted to this account (no new JE)
 *  - kind 'undeposited'   → record DR bank / CR Undeposited Funds (money received but not yet deposited)
 *  - kind 'transfer_pair' → pair with an unmatched txn on another account in this company.
 *                           Posts a transfer JE: DR money-in side / CR money-out side. Links BOTH txns.
 */
export interface MatchCandidate {
  kind: 'existing' | 'undeposited' | 'transfer_pair' | 'receivable' | 'payable';
  /** 'existing' / 'receivable' / 'payable': the journal entry to link or settle. */
  journalEntryId: string | null;
  entryNumber: string | null;
  /**
   * 'receivable': the A/R DEBIT line this deposit would clear. Settling posts
   * DR bank / CR A/R and deliberately does NOT touch revenue — the accrual
   * already recognised it, and recognising it again is the double-count the
   * whole accrual flow exists to prevent.
   */
  receivableLineId: string | null;
  /** 'receivable': still outstanding on that accrual, 2dp. */
  receivableOutstanding: string | null;
  /** 'undeposited': the Undeposited Funds account to credit. */
  offsetAccountId: string | null;
  /** 'transfer_pair': the OTHER bank transaction this would pair with. */
  pairBankTransactionId: string | null;
  /** 'transfer_pair': the other bank account's display label (e.g. "Chase Checking"). */
  pairBankAccountLabel: string | null;
  date: string | null;
  /** Human description, e.g. "JE-00012 · Deposit from Acme" or "Pair with Chase Credit Card +$245.00 on Mar 5". */
  label: string;
  /** Net movement on the bank GL (existing) or the deposit amount (undeposited/transfer_pair), 2dp. */
  amount: string;
  /**
   * The top non-bank line of the existing journal entry (so the user can
   * disambiguate similar amounts at a glance). Only populated for kind='existing'.
   */
  topOffsetAccountCode: string | null;
  topOffsetAccountName: string | null;
  /** Memo from the journal entry (NOT the line — that's not always set). */
  entryMemo: string | null;
  /**
   * 'payable': the accrual CREDIT line this payment would clear (e.g. one
   * person's net pay sitting in "Uncleared wage cheques"). Settling posts
   * DR liability / CR bank — it does NOT touch wage expense, which the payroll
   * run already recorded. Categorising the payment as an expense instead is the
   * double-count the holding account exists to prevent.
   */
  payableLineId: string | null;
  /** 'payable': who the accrual is owed to, when the payroll module named them. */
  payablePartyName: string | null;
}

/** Settle one bank payment against one or more open payroll accruals. */
export const settlePayablesInputSchema = z.object({
  /** Accrual lines to clear. Their outstanding amounts must total the payment. */
  journalLineIds: z.array(z.string().uuid()).min(1).max(200),
});
export type SettlePayablesInput = z.infer<typeof settlePayablesInputSchema>;

/** One payroll accrual awaiting payment, offered for tick-box settlement. */
export interface OpenPayableItem {
  journalLineId: string;
  journalEntryId: string;
  entryNumber: string;
  date: string;
  memo: string | null;
  accountId: string;
  accountCode: string;
  accountName: string;
  partyName: string | null;
  outstanding: string;
}

/** Match a bank transaction as an intercompany advance with another company. */
export const matchBankAsIntercompanyInputSchema = z.object({
  otherCompanyId: z.string().uuid(),
  otherAccountId: z.string().uuid(), // the GL account posted on the other company's side
  description: z.string().max(500).optional(),
});
export type MatchBankAsIntercompanyInput = z.infer<
  typeof matchBankAsIntercompanyInputSchema
>;

export const listBankTransactionsQuerySchema = z.object({
  bankAccountId: z.string().uuid().optional(),
  matchStatus: z.enum(MATCH_STATUSES).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  /** Case-insensitive substring match on description OR merchant name. */
  q: z.string().max(200).optional(),
  /**
   * Also return rows still pending at the bank. They come back with
   * `pending: true` and cannot be matched, excluded or linked until they
   * settle (the bank issues a new id at settlement). Off by default so every
   * other consumer keeps seeing settled rows only.
   */
  includePending: z
    .preprocess((v) => (v === undefined || v === null ? undefined : v === true || v === 'true' || v === '1'), z.boolean().optional()),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListBankTransactionsQuery = z.infer<
  typeof listBankTransactionsQuerySchema
>;

// ── Bank rule ────────────────────────────────────────────────────────────────

const moneyString = z
  .string()
  .regex(/^-?\d+(\.\d{1,4})?$/, 'must be a money value with up to 4 decimals');

/**
 * An extra line a rule carves out of each matched amount before the target
 * account takes the remainder. The shape stored in bank_rules.splits.
 */
export const bankRuleSplitSchema = z
  .object({
    accountId: z.string().uuid(),
    kind: z.enum(['percent', 'amount']),
    value: z.string().regex(/^\d+(\.\d{1,4})?$/, 'must be a positive number'),
    memo: z.string().max(500).nullable().optional(),
  })
  .refine((s) => Number(s.value) > 0, { message: 'split value must be greater than zero' })
  .refine((s) => s.kind !== 'percent' || Number(s.value) <= 100, {
    message: 'a percent split cannot exceed 100',
  });
export const bankRuleSplitsSchema = z
  .array(bankRuleSplitSchema)
  .max(10)
  .refine(
    (arr) =>
      arr
        .filter((s) => s.kind === 'percent')
        .reduce((sum, s) => sum + Number(s.value), 0) <= 100,
    { message: 'percent splits cannot total more than 100' },
  );

export const createBankRuleInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    priority: z.number().int().min(1).max(10000).optional(),
    bankAccountId: z.string().uuid().nullable().optional(),
    matchField: z.enum(RULE_MATCH_FIELDS),
    matchType: z.enum(RULE_MATCH_TYPES).optional(),
    matchValue: z.string().max(500).optional(),
    amountMin: moneyString.optional(),
    amountMax: moneyString.optional(),
    targetAccountId: z.string().uuid(),
    targetContactId: z.string().uuid().nullable().optional(),
    targetLocationId: z.string().uuid().nullable().optional(),
    targetClassId: z.string().uuid().nullable().optional(),
    memoTemplate: z.string().max(500).optional(),
    /** Extra offset lines; the target account takes the remainder. */
    splits: bankRuleSplitsSchema.optional(),
    autoPost: z.boolean().default(false),
    requireReviewAboveAmount: moneyString.optional(),
  })
  .refine(
    (data) => {
      // Text-based match fields need matchType + matchValue
      const needsText =
        data.matchField === 'description' ||
        data.matchField === 'merchant_name' ||
        data.matchField === 'description_or_merchant' ||
        data.matchField === 'description_and_amount';
      if (needsText && (!data.matchType || !data.matchValue)) return false;
      // amount_range needs at least one of amountMin/amountMax
      const needsAmount =
        data.matchField === 'amount_range' ||
        data.matchField === 'description_and_amount';
      if (needsAmount && !data.amountMin && !data.amountMax) return false;
      return true;
    },
    {
      message:
        'matchField requires the corresponding matchType+matchValue or amount range',
    },
  );
export type CreateBankRuleInput = z.infer<typeof createBankRuleInputSchema>;

export const updateBankRuleInputSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  priority: z.number().int().min(1).max(10000).optional(),
  bankAccountId: z.string().uuid().nullable().optional(),
  matchField: z.enum(RULE_MATCH_FIELDS).optional(),
  matchType: z.enum(RULE_MATCH_TYPES).nullable().optional(),
  matchValue: z.string().max(500).nullable().optional(),
  amountMin: moneyString.nullable().optional(),
  amountMax: moneyString.nullable().optional(),
  targetAccountId: z.string().uuid().optional(),
  targetContactId: z.string().uuid().nullable().optional(),
  targetLocationId: z.string().uuid().nullable().optional(),
  targetClassId: z.string().uuid().nullable().optional(),
  memoTemplate: z.string().max(500).nullable().optional(),
  /** Replace the split lines; null or [] clears them. */
  splits: bankRuleSplitsSchema.nullable().optional(),
  autoPost: z.boolean().optional(),
  requireReviewAboveAmount: moneyString.nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateBankRuleInput = z.infer<typeof updateBankRuleInputSchema>;

export const reorderBankRulesInputSchema = z.object({
  orderedIds: z.array(z.string().uuid()).min(1),
});
export type ReorderBankRulesInput = z.infer<typeof reorderBankRulesInputSchema>;

export const bankRuleSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string(),
  priority: z.number(),
  bankAccountId: z.string().uuid().nullable(),
  matchField: z.enum(RULE_MATCH_FIELDS),
  matchType: z.enum(RULE_MATCH_TYPES).nullable(),
  matchValue: z.string().nullable(),
  amountMin: z.string().nullable(),
  amountMax: z.string().nullable(),
  targetAccountId: z.string().uuid(),
  targetContactId: z.string().uuid().nullable(),
  targetLocationId: z.string().uuid().nullable(),
  targetClassId: z.string().uuid().nullable(),
  memoTemplate: z.string().nullable(),
  splits: z.array(bankRuleSplitSchema).nullable(),
  autoPost: z.boolean(),
  requireReviewAboveAmount: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BankRule = z.infer<typeof bankRuleSchema>;

// ── Reclassify by account (filter-driven sweep) ──────────────────────────────

const RECLASSIFY_ISO_DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');

/**
 * Bank-side reclassify reasons. Mirrors RECLASSIFY_REASONS in
 * journal.schemas.ts (kept as a plain list here so this file stays free of
 * cross-schema imports). The default, `misclassified`, is what a one-off
 * re-point from the feed means in practice.
 */
export const BANK_RECLASSIFY_REASON_CODES = [
  'misclassified',
  'payroll_breakout',
  'split_detail',
  'personal_business',
  'capitalize_expense',
  'contact_correction',
  'coa_cleanup',
  'accountant_adjustment',
  'other',
] as const;
export type BankReclassifyReasonCode = (typeof BANK_RECLASSIFY_REASON_CODES)[number];
export const bankReclassifyReasonCodeSchema = z.enum(BANK_RECLASSIFY_REASON_CODES);

/** Optional reason fields accepted by the single and bulk reclassify routes. */
export const bankReclassifyReasonSchema = z.object({
  reasonCode: bankReclassifyReasonCodeSchema.optional(),
  reasonNote: z.string().max(500).nullable().optional(),
});

/**
 * Move every linked bank transaction in a date range whose offset sits on one
 * account onto another account. Entries created by a bank match are moved
 * through the bank-side reclassify; entries created by import or by hand that
 * have the same single-offset shape are moved through the GL reclassify (the
 * candidate carries `via: 'entry'`). Deposit documents, checks and every other
 * source linked to a bank row are reported as skipped rather than touched.
 *
 * Import and manual entries that were never linked to a bank row at all (a
 * QuickBooks import brought in before the bank feed existed) are swept too,
 * when they are exactly one bank-account line plus one offset. Those
 * candidates carry `linked: false` and are keyed by the entry id.
 */
export const reclassifyByAccountInputSchema = z
  .object({
    fromAccountId: z.string().uuid(),
    toAccountId: z.string().uuid(),
    from: RECLASSIFY_ISO_DATE,
    to: RECLASSIFY_ISO_DATE,
    /**
     * Case-insensitive substring on the bank description; for an unlinked
     * entry, on the entry memo (where the QuickBooks import keeps its text).
     */
    descriptionContains: z.string().trim().max(200).optional(),
    /** True (the default) previews only; nothing is written. */
    dryRun: z.boolean().default(true),
    reasonCode: bankReclassifyReasonCodeSchema.default('misclassified'),
    reasonNote: z.string().max(500).nullable().optional(),
  })
  .refine((v) => v.from <= v.to, { message: 'from must be on or before to', path: ['to'] })
  .refine((v) => v.fromAccountId !== v.toAccountId, {
    message: 'from and to accounts must differ',
    path: ['toAccountId'],
  });
export type ReclassifyByAccountInput = z.infer<typeof reclassifyByAccountInputSchema>;

/** One apply call moves at most this many rows; the client loops for the rest. */
export const RECLASSIFY_BY_ACCOUNT_MAX_APPLY = 2000;

export interface ReclassifyByAccountCandidate {
  /** Bank transaction id when linked; the journal entry id when not. */
  id: string;
  date: string;
  /** Plaid-sign amount: negative is money into the bank account. */
  amount: string;
  description: string;
  /** Bank row match state; 'unlinked' when no bank row points at the entry. */
  matchStatus: string;
  entrySource: string;
  entryNumber: string;
  /**
   * How the row is moved: 'bank' re-points the offset line of a bank-match
   * entry in place; 'entry' resubmits an import or manual entry's full line
   * set through the GL reclassify with only the offset account changed.
   */
  via: 'bank' | 'entry';
  /**
   * False when nothing in the bank feed points at the entry: no bank row
   * matched to it and no line carrying a bank transaction id. Such entries
   * are always moved via 'entry'.
   */
  linked: boolean;
}

export interface ReclassifyByAccountSkipped {
  /** Bank transaction id when linked; the journal entry id when not. */
  id: string;
  date: string;
  amount: string;
  description: string;
  reason: string;
}

export interface ReclassifyByAccountResult {
  dryRun: boolean;
  candidates: ReclassifyByAccountCandidate[];
  skipped: ReclassifyByAccountSkipped[];
  totals: {
    candidateCount: number;
    /** Sum of the absolute bank amounts of the candidates, 4dp string. */
    candidateAmount: string;
    skippedCount: number;
  };
  /** Rows moved by this call (0 on a dry run). */
  applied: number;
  failed: { id: string; reason: string }[];
  /** Candidates beyond the per-call cap that still need another apply call. */
  remaining: number;
}
